import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, CalendarClock, XCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import DateTimePicker from "@/components/DateTimePicker";

const META = {
  completed: { title: "Mark Meeting Done", icon: CheckCircle2, tone: "text-emerald-600", btn: "Mark as Done", btnCls: "bg-emerald-600 hover:bg-emerald-700" },
  rescheduled: { title: "Reschedule Meeting", icon: CalendarClock, tone: "text-loop", btn: "Reschedule", btnCls: "bg-loop hover:bg-loophover" },
  cancelled: { title: "Cancel Meeting", icon: XCircle, tone: "text-red-600", btn: "Cancel Meeting", btnCls: "bg-red-600 hover:bg-red-700" },
};

const blank = { outcome: "", notes: "", next_follow_up_at: "", due_at: "", reason: "" };

export default function MeetingActionModal({ open, onOpenChange, action, mode = "single", meeting, ids = [], outcomes = [], onDone }) {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const isBulk = mode === "bulk";
  const meta = META[action] || META.completed;
  const Icon = meta.icon;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (open) setForm(blank); }, [open, action, mode]);

  const submit = async () => {
    if (action === "completed" && !form.outcome) { toast.error("Please select a meeting outcome"); return; }
    if (action === "rescheduled" && !form.due_at) { toast.error("Please pick a new date & time"); return; }
    setSaving(true);
    try {
      if (isBulk) {
        const body = { meeting_ids: ids, action };
        if (form.outcome) body.outcome = form.outcome;
        if (form.notes) body.notes = form.notes;
        if (form.next_follow_up_at) body.next_follow_up_at = form.next_follow_up_at;
        if (form.due_at) body.due_at = form.due_at;
        if (form.reason) body.reason = form.reason;
        const { data } = await api.patch("/activities/meetings/bulk-status", body);
        const d = data.data;
        const base = action === "completed" ? "Selected meetings marked as done"
          : action === "rescheduled" ? "Selected meetings rescheduled" : "Selected meetings cancelled";
        toast.success(`${base} · ${d.updated} updated${d.skipped ? `, ${d.skipped} skipped` : ""}`);
      } else {
        const body = { action };
        if (form.outcome) body.outcome = form.outcome;
        if (form.notes) body.notes = form.notes;
        if (form.next_follow_up_at) body.next_follow_up_at = form.next_follow_up_at;
        if (form.due_at) body.due_at = form.due_at;
        if (form.reason) body.reason = form.reason;
        await api.patch(`/activities/meetings/${meeting.id}/status`, body);
        toast.success(action === "completed" ? "Meeting marked as done"
          : action === "rescheduled" ? "Meeting rescheduled successfully" : "Meeting cancelled");
      }
      onOpenChange(false);
      onDone && onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const subject = isBulk ? `${ids.length} selected meeting${ids.length === 1 ? "" : "s"}` : (meeting?.lead_name || "this meeting");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={`meeting-action-modal-${action}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icon className={`h-5 w-5 ${meta.tone}`} />{meta.title}</DialogTitle>
          <DialogDescription>
            {isBulk ? `This applies to ${subject}.` : `Meeting with ${subject}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {action === "completed" && (
            <>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Meeting outcome <span className="text-red-500">*</span></label>
                <select data-testid="meeting-outcome-select" value={form.outcome} onChange={(e) => set("outcome", e.target.value)} className="h-10 w-full rounded-lg border border-sky bg-white px-2 text-sm text-navy outline-none focus:border-loop">
                  <option value="">Select an outcome…</option>
                  {outcomes.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">{isBulk ? "Common note" : "Meeting notes"}</label>
                <textarea data-testid="meeting-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder="What was discussed, decisions, next steps…" className="w-full rounded-lg border border-sky bg-white px-3 py-2 text-sm text-navy outline-none focus:border-loop" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Next follow-up {isBulk ? "(applied to all)" : ""} <span className="font-normal text-ink/45">— optional</span></label>
                <DateTimePicker value={form.next_follow_up_at} onChange={(v) => set("next_follow_up_at", v)} testid="meeting-next-followup" placeholder="Schedule a follow-up" />
                <p className="mt-1 text-[11px] text-ink/45">If set, a follow-up activity is created on the lead.</p>
              </div>
            </>
          )}

          {action === "rescheduled" && (
            <>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">New date & time <span className="text-red-500">*</span></label>
                <DateTimePicker value={form.due_at} onChange={(v) => set("due_at", v)} testid="meeting-reschedule-datetime" placeholder="Pick new date & time" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Reschedule reason <span className="font-normal text-ink/45">— optional</span></label>
                <textarea data-testid="meeting-reason-input" value={form.reason} onChange={(e) => set("reason", e.target.value)} rows={2} placeholder="Why is this being rescheduled?" className="w-full rounded-lg border border-sky bg-white px-3 py-2 text-sm text-navy outline-none focus:border-loop" />
              </div>
              {isBulk && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">This will reschedule all selected meetings to the same date and time. Assignees and participants stay the same.</p>}
            </>
          )}

          {action === "cancelled" && (
            <>
              <p className="text-sm text-ink/70">Are you sure you want to cancel {isBulk ? subject : "this meeting"}? Cancelled meetings stay visible under the Cancelled status filter.</p>
              <div>
                <label className="mb-1 block text-xs font-semibold text-navy">Cancellation reason <span className="font-normal text-ink/45">— optional</span></label>
                <textarea data-testid="meeting-reason-input" value={form.reason} onChange={(e) => set("reason", e.target.value)} rows={2} placeholder="Reason for cancellation" className="w-full rounded-lg border border-sky bg-white px-3 py-2 text-sm text-navy outline-none focus:border-loop" />
              </div>
            </>
          )}
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} disabled={saving} data-testid="meeting-action-cancel" className="rounded-lg border border-sky px-4 py-2 text-sm font-medium text-ink/70 hover:bg-mist disabled:opacity-50">Close</button>
          <button onClick={submit} disabled={saving} data-testid="meeting-action-submit" className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${meta.btnCls}`}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}{meta.btn}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
